import { useCallback } from 'react';

import { AttachmentResponse, PostgresFilePointer } from '@/api/data-contracts';
import { useMediaPlansService } from '@/features/media-plans';
import { useOrdersService } from '@/features/orders';
import { useAdvertisingSpacesService } from '@/features/spaces';
import useArtworkService from '@/shared/api/artwork.service';

const useVersions = () => {
  const {
    downloadMediaPlanDocumentById,
    uploadMediaPlanAttachmentVersion,
    setAttachmentActiveVersion: setMediaPlanAttachmentActiveVersion,
    deleteAttachmentVersion: deleteMediaPlanAttachmentVersion,
  } = useMediaPlansService();
  const {
    downloadOrderDocumentById,
    uploadOrderAttachmentVersion,
    setAttachmentActiveVersion: setOrderAttachmentActiveVersion,
    deleteAttachmentVersion: deleteOrderAttachmentVersion,
  } = useOrdersService();
  const { downloadSpaceDocumentById } = useAdvertisingSpacesService();
  const { downloadArtworkDocumentForOrder } = useArtworkService();

  const downloadMediaPlanFiles = useCallback(
    async (filePointers: PostgresFilePointer[], mediaPlanId: string): Promise<File[]> => {
      const files = [];
      for (let i = 0; i < filePointers.length; i++) {
        const blob = await downloadMediaPlanDocumentById(
          mediaPlanId!,
          filePointers[i].objectId!.toString(),
        );

        files[i] = new File([blob], filePointers[i].name!);
      }

      return files;
    },
    // eslint-disable-next-line
    [],
  );

  const downloadArtworkFile = useCallback(
    async (filePointer: PostgresFilePointer, orderId: string, bookingId: string): Promise<File> => {
      const blob = await downloadArtworkDocumentForOrder(
        orderId!,
        bookingId!,
        filePointer.objectId!,
      );
      return new File([blob], filePointer.name!);
    },
    // eslint-disable-next-line
    [],
  );

  const downloadOrderFiles = useCallback(
    async (
      filePointers: PostgresFilePointer[],
      orderId: string,
      bookingId: string | undefined,
    ): Promise<File[]> => {
      const files = [];
      for (let i = 0; i < filePointers.length; i++) {
        try {
          const blob = await downloadOrderDocumentById(
            orderId!,
            filePointers[i].objectId!.toString(),
          );
          files[i] = new File([blob], filePointers[i].name as string);
        } catch {
          if (bookingId) {
            try {
              files[i] = await downloadArtworkFile(filePointers[i], orderId, bookingId);
              // eslint-disable-next-line no-empty
            } catch {}
          }
        }
      }

      return files;
    },
    // eslint-disable-next-line
    [downloadArtworkFile],
  );

  const downloadAdvertisingSpaceFiles = useCallback(
    async (filePointers: PostgresFilePointer[], advertisingSpaceId: string): Promise<File[]> => {
      const files = [];
      for (let i = 0; i < filePointers.length; i++) {
        const blob = await downloadSpaceDocumentById(
          advertisingSpaceId!,
          filePointers[i].objectId!.toString(),
        );

        files[i] = new File([blob], filePointers[i].name as string);
      }

      return files;
    },
    // eslint-disable-next-line
    [],
  );

  const downloadAttachments = useCallback(
    async (
      filePointers: PostgresFilePointer[],
      {
        orderId,
        bookingId,
        mediaPlanId,
        advertisingSpaceId,
      }: {
        orderId: string | undefined;
        bookingId: string | undefined;
        mediaPlanId: string | undefined;
        advertisingSpaceId: string | undefined;
      },
    ): Promise<File[]> => {
      if (orderId) {
        return await downloadOrderFiles(filePointers, orderId, bookingId);
      } else if (mediaPlanId) {
        return await downloadMediaPlanFiles(filePointers, mediaPlanId);
      } else if (advertisingSpaceId) {
        return await downloadAdvertisingSpaceFiles(filePointers, advertisingSpaceId);
      } else {
        return [];
      }
    },
    [downloadAdvertisingSpaceFiles, downloadMediaPlanFiles, downloadOrderFiles],
  );

  const uploadVersion = useCallback(
    async ({
      attachmentId,
      category,
      keyVisuals,
      artWork,
      orderId,
      mediaPlanId,
    }: {
      attachmentId: string;
      category: string;
      keyVisuals: File[];
      artWork: File[];
      orderId?: string;
      mediaPlanId?: string;
    }): Promise<AttachmentResponse | undefined> => {
      if (mediaPlanId) {
        return await uploadMediaPlanAttachmentVersion(mediaPlanId, attachmentId, category, {
          keyVisuals,
          artWork,
        });
      } else if (orderId) {
        return await uploadOrderAttachmentVersion(orderId, attachmentId, category, {
          keyVisuals,
          artWork,
        });
      }
    },
    [uploadMediaPlanAttachmentVersion, uploadOrderAttachmentVersion],
  );

  const setActiveVersion = useCallback(
    async ({
      attachmentId,
      orderId,
      mediaPlanId,
      versionId,
    }: {
      attachmentId: string;
      orderId?: string;
      mediaPlanId?: string;
      versionId: number;
    }): Promise<AttachmentResponse | undefined> => {
      if (orderId) {
        return await setOrderAttachmentActiveVersion(orderId, attachmentId, versionId);
      } else if (mediaPlanId) {
        return await setMediaPlanAttachmentActiveVersion(mediaPlanId, attachmentId, versionId);
      }
    },
    [setMediaPlanAttachmentActiveVersion, setOrderAttachmentActiveVersion],
  );

  const deleteVersion = useCallback(
    async ({
      attachmentId,
      orderId,
      mediaPlanId,
      versionId,
    }: {
      attachmentId: string;
      orderId?: string;
      mediaPlanId?: string;
      versionId: number;
    }): Promise<AttachmentResponse | undefined> => {
      if (orderId) {
        return await deleteOrderAttachmentVersion(orderId, attachmentId, versionId);
      } else if (mediaPlanId) {
        return await deleteMediaPlanAttachmentVersion(mediaPlanId, attachmentId, versionId);
      }
    },
    [deleteMediaPlanAttachmentVersion, deleteOrderAttachmentVersion],
  );

  return { downloadAttachments, uploadVersion, setActiveVersion, deleteVersion };
};

export default useVersions;
